// src/utils/Onboarding/ResultMapping.js

/**
 * Robust extraction + mapping helpers for remote hair analysis payloads.
 * We accept multiple payload shapes and pick the first path that yields
 * a valid `analysis` object with required keys.
 */

// ---------- PATH-AWARE EXTRACTION ----------

export function extractAnalysis(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const candidates = [
    { name: 'operation.analysis', get: (p) => p?.operation?.analysis },
    { name: 'data.analysis',      get: (p) => p?.data?.analysis },
    { name: 'analysis',           get: (p) => p?.analysis },
    { name: 'result.analysis',    get: (p) => p?.result?.analysis },

    // tolerant fallbacks (some servers put fields directly under operation/data)
    { name: 'operation',          get: (p) => p?.operation },
    { name: 'data',               get: (p) => p?.data },
  ];

  for (const c of candidates) {
    const cand = c.get(payload);
    if (isValidAnalysisObject(cand)) {
      return cand;
    }
  }

  const shallow = shallowScanForAnalysis(payload);
  if (shallow) {
    return shallow;
  }

  return null;
}

function isValidAnalysisObject(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const hasBuckets =
    typeof obj.baldness_bucket === 'string' &&
    typeof obj.hairline_bucket === 'string';
  const hasConfidence = typeof obj.confidence === 'number';
  return hasBuckets && hasConfidence;
}

function shallowScanForAnalysis(payload) {
  for (const k of Object.keys(payload)) {
    const v = payload[k];
    if (isValidAnalysisObject(v)) return v;
    if (v && typeof v === 'object') {
      for (const k2 of Object.keys(v)) {
        const v2 = v[k2];
        if (isValidAnalysisObject(v2)) return v2;
      }
    }
  }
  return null;
}

// ---------- TEXT MAPPERS ----------

export function mapBaldnessBucket(bucket) {
  switch (bucket) {
    case 'none':       return 'None';
    case 'minimal':    return 'Low';
    case 'moderate':   return 'Medium';
    case 'severe':     return 'High';
    case 'full_bald':  return 'Bald';
    default:           return '—';
  }
}

export function mapHairlineBucket(bucket) {
  switch (bucket) {
    case 'none':       return 'Min';
    case 'minimal':    return 'Low';
    case 'moderate':   return 'Medium';
    case 'severe':     return 'High';
    case 'full_bald':  return 'Bald';
    default:           return '—';
  }
}

export function mapConfidenceLabel(c) {
  const v = typeof c === 'number' ? c : 0;
  if (v > 0.75) return 'Good';
  if (v > 0.6)  return 'Okay';
  if (v > 0.5)  return 'Adequate';
  return 'Low';
}

// ---------- ENHANCED MAPPINGS ----------

const BUCKET_SCORE_MAP = {
  none: 0,
  minimal: 1,
  moderate: 2,
  severe: 3,
  full_bald: 4,
};

const SEVERITY_COLOR_MAP = {
  0: 'text-emerald-400',
  1: 'text-lime-300',
  2: 'text-yellow-300',
  3: 'text-orange-400',
  4: 'text-red-500',
};

const FIXABILITY_COLOR_MAP = {
  High: 'text-emerald-400',
  Medium: 'text-amber-300',
  Low: 'text-red-500',
};

export function bucketToScoreValue(bucket) {
  return BUCKET_SCORE_MAP[bucket] ?? 0;
}

export function getSeverityColorClass(bucket) {
  const score = bucketToScoreValue(bucket);
  return SEVERITY_COLOR_MAP[score] || 'text-white/70';
}

export function mapFixabilityForBaldness(bucket) {
  const score = bucketToScoreValue(bucket);
  let label = 'High';
  if (score >= 5) label = 'Low';
  else if (score >= 3) label = 'Medium';
  return {
    label,
    colorClass: FIXABILITY_COLOR_MAP[label] || 'text-white/70',
  };
}

export function computeNorwoodScore(baldnessBucket, hairlineBucket) {
  const total = bucketToScoreValue(baldnessBucket) + bucketToScoreValue(hairlineBucket);
  const clamped = Math.max(0, Math.min(8, total));
  const scaled = Math.round((clamped / 8) * 6) + 1; // map 0→1, 8→7
  return Math.max(1, Math.min(7, scaled));
}
